import { useBreakpointDown } from '@all-manager/design-system-v2';
import { type ChangeEvent, type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import { useGlobalSearch } from '../../api/useGlobalSearch';
import { GlobalSearchResults, SearchItemType, SearchResultItem } from '../../types';

type HookProps = {
  isOpen: boolean;
  onClose: () => void;
};

type SearchView = 'idle' | 'loading' | 'error' | 'empty' | 'results';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;
const FOCUS_DELAY_MS = 50;

const GROUPS: { type: SearchItemType; label: string; key: keyof Omit<GlobalSearchResults, 'totalCount'> }[] = [
  { type: 'sub_topic', label: 'Sujets', key: 'subTopics' },
  { type: 'provider', label: 'Prestataires', key: 'providers' },
  { type: 'document', label: 'Documents', key: 'documents' },
  { type: 'meeting', label: 'Réunions', key: 'meetings' },
];

function isExternalDocument(item: SearchResultItem) {
  return item.type === 'document' && item.url.startsWith('http');
}

function getView(params: { isQueryTooShort: boolean; isLoading: boolean; isError: boolean; count: number }): SearchView {
  if (params.isQueryTooShort) return 'idle';
  if (params.isLoading) return 'loading';
  if (params.isError) return 'error';
  return params.count === 0 ? 'empty' : 'results';
}

export function useGlobalSearchModal(props: HookProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const debouncedSearch = useDebouncedValue(searchTerm, DEBOUNCE_MS);
  const isPhone = useBreakpointDown('sm');
  const navigate = useNavigate();

  const { data, isLoading, isError, refetch } = useGlobalSearch(debouncedSearch);
  const isQueryTooShort = debouncedSearch.trim().length < MIN_QUERY_LENGTH;

  // Focus the input once the dialog is mounted (autoFocus is not enough after the opening transition).
  useEffect(() => {
    if (!props.isOpen) {
      return;
    }
    const timer = setTimeout(() => inputRef.current?.focus(), FOCUS_DELAY_MS);
    return () => clearTimeout(timer);
  }, [props.isOpen]);

  // Reset search and selection when the dialog closes.
  useEffect(() => {
    if (!props.isOpen) {
      setSearchTerm('');
      setSelectedIndex(0);
    }
  }, [props.isOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [debouncedSearch]);

  // Flattened in display order for keyboard navigation.
  const allResults: SearchResultItem[] = GROUPS.flatMap((group) => data?.[group.key] ?? []);
  const view = getView({ isQueryTooShort, isLoading, isError, count: allResults.length });

  function optionId(index: number) {
    return `${listboxId}-option-${index}`;
  }

  let offset = 0;
  const groups = GROUPS.map((group) => {
    const items = (data?.[group.key] ?? []).map((item, position) => {
      const index = offset + position;
      return {
        item,
        index,
        optionId: optionId(index),
        isActive: index === selectedIndex,
        isExternal: isExternalDocument(item),
      };
    });
    offset += items.length;
    return { type: group.type, label: `${group.label} (${items.length})`, items };
  }).filter((group) => group.items.length > 0);

  function handleSelect(item: SearchResultItem) {
    props.onClose();
    if (isExternalDocument(item)) {
      window.open(item.url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(item.url);
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((previous) => (allResults.length > 0 ? (previous + 1) % allResults.length : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((previous) => (allResults.length > 0 ? (previous - 1 + allResults.length) % allResults.length : 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (allResults[selectedIndex]) {
        handleSelect(allResults[selectedIndex]);
      }
    }
  }

  function handleSearchTermChange(event: ChangeEvent<HTMLInputElement>) {
    setSearchTerm(event.target.value);
  }

  function handleClear() {
    setSearchTerm('');
    inputRef.current?.focus();
  }

  function handleHover(index: number) {
    setSelectedIndex(index);
  }

  function handleRetry() {
    void refetch();
  }

  const hasResults = view === 'results';
  const count = allResults.length;

  return {
    inputRef,
    listboxId,
    searchTerm,
    debouncedSearch,
    view,
    groups,
    hasResults,
    activeOptionId: hasResults && allResults[selectedIndex] ? optionId(selectedIndex) : undefined,
    statusMessage: hasResults ? `${count} résultat${count > 1 ? 's' : ''}` : '',
    showKeyboardHint: hasResults && !isPhone,
    handleSelect,
    handleKeyDown,
    handleSearchTermChange,
    handleClear,
    handleHover,
    handleRetry,
  };
}
